import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { getAllHomeBannersAdmin, deleteHomeBanner } from "../../Apis/HomebannersApi";

const HomeBannersList = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const navigate = useNavigate();

    const fetchBanners = async () => {
        try {
            setLoading(true);
            const data = await getAllHomeBannersAdmin();
            const bannersArray = Array.isArray(data) ? data : data.data || [];
            setBanners(bannersArray);
            setErrorMessage("");
        } catch (error) {
            console.error("Error fetching banners:", error);
            setErrorMessage("Failed to load banners.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const confirmDelete = (id) => {
        setSelectedId(id);
        setOpenConfirm(true);
    };

    const handleDelete = async () => {
        try {
            await deleteHomeBanner(selectedId);
            setBanners((prev) => prev.filter((b) => (b.id || b._id) !== selectedId));
            setSnackbar({
                open: true,
                message: "Banner deleted successfully!",
                severity: "success",
            });
        } catch (error) {
            console.error("Delete error:", error);
            setSnackbar({
                open: true,
                message: "Failed to delete banner.",
                severity: "error",
            });
        } finally {
            setOpenConfirm(false);
            setSelectedId(null);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return <CircularProgress sx={{ display: "block", margin: "auto" }} />;
    }

    if (errorMessage) {
        return <Alert severity="error" sx={{ mt: 3 }}>{errorMessage}</Alert>;
    }

    return (
        <Box sx={{ p: 3, width: "95%", mx: "auto" }}>
            <Typography variant="h4" gutterBottom textAlign="start" fontWeight="bold">
                Home Banners
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button variant="contained" onClick={() => navigate("/homebanners/add")}>
                    + Add Banner
                </Button>
            </Box>

            <Grid container spacing={4}>
                {banners.length > 0 ? (
                    banners.map((banner) => (
                        <Grid item xs={12} sm={12} md={10} lg={8} key={banner.id || banner._id} sx={{ width: "28%" }} >
                            <Card
                                elevation={3}
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    transition: "none", // remove hover effect

                                }}
                            >
                                {banner.imageUrl ? (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: 0,
                                            paddingTop: "50%", // taller banner
                                            position: "relative",
                                        }}
                                    >
                                        <img
                                            src={banner.imageUrl}
                                            alt="Banner"
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                // padding: "15px 15px",
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            height: 300, // increased height
                                            backgroundColor: "#f0f0f0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography>No Image</Typography>
                                    </Box>
                                )}

                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        Order: {banner.order}
                                    </Typography>
                                </CardContent>

                                <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ minHeight: 30, padding: "4px 12px", fontSize: "0.75rem" }}
                                        onClick={() =>
                                            navigate(`/homebanners/edit/${banner.id || banner._id}`)
                                        }
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="contained"
                                        sx={{ minHeight: 30, padding: "4px 12px", fontSize: "0.75rem" }}
                                        onClick={() => confirmDelete(banner.id || banner._id)}
                                    >
                                        Delete
                                    </Button>
                                </CardActions>

                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography variant="body1" textAlign="center" sx={{ mt: 5 }}>
                            No banners available.
                        </Typography>
                    </Grid>
                )}
            </Grid>






            {/* Delete Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this banner? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>

    );
};

export default HomeBannersList;
